import {
  doClearEntireEntriesWithIds,
  doClearEntireFieldsWithIds,
  doClearValuesWithIds,
} from '@databases/redux/contextual-menu/actions';
import { appStore } from '@databases/redux/store';
import { createClearCellsEffect } from '@databases/selections';
import { getService } from '@databases/service-container';
import { TABLE_V2_LAYOUT_ID } from '@databases/view-layout-table-v2/id';

import {
  type SelectionEffectFeature,
  SelectionFeatureKeys,
} from '../../features/types.ts';

export function createClearCellsEffectFeature(deps: {
  contentId: string;
}): SelectionEffectFeature {
  return {
    key: SelectionFeatureKeys.CLEAR_CELLS_EFFECT,
    type: 'effect',
    isEnabled: (ctx) =>
      ctx.layoutType !== TABLE_V2_LAYOUT_ID && ctx.permissions.canEditContent,
    factory: () => {
      const selectionsApi = getService('SelectionsApi');

      return createClearCellsEffect({
        // Routes to the correct Redux action per mode so each produces
        // a single YJS transaction — matching the legacy action bar routing.
        resolveSelectionToIds: () => {
          const resolved = selectionsApi.getResolvedSelection();

          if (resolved.mode === 'idle') {
            return { entryIds: [], fieldIds: [] };
          }

          if (resolved.mode === 'row') {
            return {
              entryIds: resolved.entries.map((e) => e.entryId),
              fieldIds: [],
            };
          }

          if (resolved.mode === 'column') {
            return {
              entryIds: [],
              fieldIds: resolved.fields.map((f) => f.fieldId),
            };
          }

          // Cell mode: deduplicate entry/field IDs across selected values.
          const entryIdSet = new Set<string>();
          const fieldIdSet = new Set<string>();

          for (const value of resolved.values) {
            entryIdSet.add(value.entryId);
            fieldIdSet.add(value.fieldId);
          }

          return {
            entryIds: [...entryIdSet],
            fieldIds: [...fieldIdSet],
          };
        },

        onClearValues: (entryIds: string[], fieldIds: string[]) => {
          const contentId = deps.contentId;

          if (entryIds.length > 0 && fieldIds.length === 0) {
            appStore.dispatch(doClearEntireEntriesWithIds(contentId, entryIds)); // row
          } else if (fieldIds.length > 0 && entryIds.length === 0) {
            appStore.dispatch(doClearEntireFieldsWithIds(contentId, fieldIds)); // column
          } else {
            appStore.dispatch(doClearValuesWithIds(contentId, entryIds, fieldIds)); // cell
          }
        },
      });
    },
  };
}
